import { supabase } from '../lib/supabase';
import { SupabaseOpportunityHistoryRepository } from '../infrastructure/repositories/SupabaseOpportunityHistoryRepository';


export type EntityType = 'conversation' | 'contact' | 'opportunity';

interface SyncParams {
    entityType: EntityType;
    entityId: string;
    userId: string | null;
    phone?: string;
    organizationId: string;
}

interface BroadcastMessage {
    phone: string;
    userId: string | null;
    organizationId: string;
}

class AssignmentSyncService {
    private broadcastChannel: BroadcastChannel | null = null;

    constructor() {
        // Inicializa BroadcastChannel para sincronização entre abas
        if (typeof window !== 'undefined') {
            try {
                this.broadcastChannel = new BroadcastChannel('adlitics_assignment_sync');
                this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
                    // Re-dispara evento na aba atual quando recebe mensagem de outra aba
                    const { phone, userId, organizationId } = event.data;
                    console.log('[AssignmentSync] Mensagem broadcast recebida:', event.data);

                    window.dispatchEvent(new CustomEvent('assignment:changed', {
                        detail: { phone, userId, organizationId, source: 'broadcast' }
                    }));
                };
            } catch (error) {
                console.warn('[AssignmentSync] BroadcastChannel não suportado:', error);
            }
        }
    }

    /**
     * Retorna cliente Supabase atualizado (garante que tem token válido)
     */
    private getSupabase() {
        return supabase;
    }

    /**
     * Limpa telefone removendo caracteres especiais
     */
    private cleanPhone(phone: string): string {
        return phone.replace('@s.whatsapp.net', '').replace(/\D/g, '');
    }

    /**
     * Sincroniza atribuição para todas as entidades vinculadas pelo telefone
     */
    async syncByPhone(
        phone: string,
        userId: string | null,
        organizationId: string,
        excludeType?: EntityType,
        excludeId?: string
    ): Promise<void> {
        if (!phone || !organizationId) return;

        const cleanedPhone = this.cleanPhone(phone);
        // Usa sufixo para match mais abrangente (handle DDI) e garante segurança mínima
        const searchTerm = cleanedPhone.length >= 12 ? cleanedPhone.slice(-11) : cleanedPhone;

        // Log para debug
        console.log('[AssignmentSync] Iniciando sync:', {
            phone, cleanedPhone, searchTerm, userId, excludeType
        });

        try {
            // 1. Busca CONTATO primeiro (pivô da relação)
            const { data: contact } = await supabase
                .from('contacts')
                .select('id')
                .eq('organization_id', organizationId)
                .or(`whatsapp.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
                .maybeSingle();

            // 2. Sincroniza CONTATO
            if (contact && excludeType !== 'contact' && contact.id !== excludeId) {
                await supabase
                    .from('contacts')
                    .update({ assigned_to: userId })
                    .eq('id', contact.id);
            }

            // 3. Sincroniza CONVERSAS via RPC por telefone.
            // Sempre — há variantes de JID / linhas duplicadas; pular quando
            // excludeType==='conversation' deixava assigned_to stale e a conversa
            // “voltava” para Meus no refresh.
            await supabase.rpc('update_conversation_assignment_by_phone', {
                p_search_term: searchTerm,
                p_organization_id: organizationId,
                p_user_id: userId
            });

            // 4. Sincroniza OPORTUNIDADES/LEADS (via Contact ID - relacional)
            if (contact && excludeType !== 'opportunity') {
                // Seleciona oportunidades afetadas primeiro para registrar histórico
                let selectQuery = supabase
                    .from('opportunities')
                    .select('id, assigned_to')
                    .eq('organization_id', organizationId)
                    .eq('contact_id', contact.id);
                
                if (excludeId) {
                    selectQuery = selectQuery.neq('id', excludeId);
                }
                
                const { data: affectedOpps } = await selectQuery;

                // Atualiza oportunidades vinculadas a este contato
                let updateQuery = supabase
                    .from('opportunities')
                    .update({ assigned_to: userId })
                    .eq('organization_id', organizationId)
                    .eq('contact_id', contact.id);

                if (excludeId) {
                    updateQuery = updateQuery.neq('id', excludeId);
                }

                await updateQuery;

                // Registra no histórico para cada oportunidade afetada
                if (affectedOpps && affectedOpps.length > 0) {
                    await Promise.all(affectedOpps.map(async (opp) => {
                        // Registra apenas se a atribuição realmente mudou
                        if (opp.assigned_to !== userId) {
                            if (userId && !opp.assigned_to) {
                                await SupabaseOpportunityHistoryRepository.addEvent({
                                    opportunityId: opp.id,
                                    organizationId,
                                    eventType: 'agent_assigned',
                                    description: 'Atendente atribuído (Auto)',
                                    newValue: userId,
                                    performedBy: userId
                                });
                            } else if (!userId && opp.assigned_to) {
                                await SupabaseOpportunityHistoryRepository.addEvent({
                                    opportunityId: opp.id,
                                    organizationId,
                                    eventType: 'agent_removed',
                                    description: 'Atendente removido (Auto)',
                                    oldValue: opp.assigned_to,
                                    performedBy: undefined
                                });
                            } else if (userId && opp.assigned_to) {
                                await SupabaseOpportunityHistoryRepository.addEvent({
                                    opportunityId: opp.id,
                                    organizationId,
                                    eventType: 'agent_assigned',
                                    description: 'Atendente alterado (Auto)',
                                    oldValue: opp.assigned_to,
                                    newValue: userId,
                                    performedBy: userId
                                });
                            }
                        }
                    }));
                }
            }

            const eventDetail = { phone: cleanedPhone, userId, organizationId };

            // Dispara evento global para notificar componentes LOCAIS
            window.dispatchEvent(new CustomEvent('assignment:changed', {
                detail: eventDetail
            }));

            // Notifica OUTRAS ABAS via BroadcastChannel
            if (this.broadcastChannel) {
                this.broadcastChannel.postMessage(eventDetail);
            }

        } catch (error) {
            console.error('[AssignmentSync] Erro:', error);
        }
    }

    /**
     * Atribui usuário a uma entidade e sincroniza com entidades vinculadas
     */
    async assign(params: SyncParams): Promise<boolean> {
        const { entityType, entityId, userId, phone, organizationId } = params;

        console.log('🟢 [AssignmentSync] assign() chamado:', { entityType, entityId, userId, phone, organizationId });

        // Verificar se há sessão ativa
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('🔐 [AssignmentSync] Session check:', {
            hasSession: !!session,
            hasToken: !!session?.access_token,
            sessionError
        });

        if (!session || sessionError) {
            console.error('❌ [AssignmentSync] SEM SESSÃO VÁLIDA!', sessionError);
            return false;
        }

        try {
            // 1. Atualiza a entidade principal
            let updateResult: any = { error: null };
            switch (entityType) {
                case 'conversation':
                    updateResult = await supabase.rpc('update_conversation_assignment', {
                        p_conversation_id: entityId,
                        p_organization_id: organizationId,
                        p_user_id: userId
                    });
                    break;

                case 'contact':
                    updateResult = await supabase
                        .from('contacts')
                        .update({ assigned_to: userId })
                        .eq('id', entityId);
                    break;

                case 'opportunity':
                    updateResult = await supabase
                        .from('opportunities')
                        .update({ assigned_to: userId })
                        .eq('id', entityId);
                    break;
            }

            if (updateResult?.error) {
                console.error('❌ [AssignmentSync] Erro no update:', updateResult.error);
                throw updateResult.error;
            }

            console.log('✅ [AssignmentSync] Update OK:', { entityType, affected: updateResult?.count });

            // 2. Sincroniza com entidades vinculadas pelo telefone
            if (phone) {
                console.log('🔄 [AssignmentSync] Iniciando syncByPhone...');
                await this.syncByPhone(phone, userId, organizationId, entityType, entityId);
            }

            return true;
        } catch (error) {
            console.error('❌ [AssignmentSync] Erro CRÍTICO:', error);
            return false;
        }
    }
}

// Singleton
export const assignmentSyncService = new AssignmentSyncService();
